<script lang="ts">
  import Button from '../common/Button.svelte';
  import FormInput from '../common/FormInput.svelte';
  import FormSelect from '../common/FormSelect.svelte';
  import SearchableSelect from '../common/SearchableSelect.svelte';
  import FormCheckbox from '../common/FormCheckbox.svelte';
  import type { NewRuleFormData } from '../../types/rule';

  let {
    formData = $bindable({} as NewRuleFormData),
    categories = [],
    people = [],
    loading = false,
    showAdvanced = $bindable(false),
    onSubmit,
    onKeydown,
    onCreateCategory
  }: {
    formData?: NewRuleFormData;
    categories: string[];
    people: string[];
    loading?: boolean;
    showAdvanced?: boolean;
    onSubmit: () => void;
    onKeydown: (event: KeyboardEvent) => void;
    onCreateCategory?: (categoryName: string) => Promise<void>;
  } = $props();

  let isFormValid = $derived(formData.pattern.trim() !== '' && formData.category !== '');
</script>

<div class="add-rule-section">
  <div class="add-rule">
    <FormInput
      label="Pattern"
      bind:value={formData.pattern}
      placeholder="e.g., 'starbucks'"
      disabled={loading}
    />
    <div class="form-field">
      <label>Category</label>
      <SearchableSelect
        bind:value={formData.category}
        options={categories}
        placeholder="Search categories..."
        onCreate={onCreateCategory}
      />
    </div>
    <FormSelect
      label="Person"
      bind:value={formData.person}
      options={['', ...people]}
      placeholder="Optional"
      disabled={loading}
    />
    <div class="button-group">
      <Button variant="primary" size="md" onclick={onSubmit} disabled={loading || !isFormValid}>
        Add Rule
      </Button>
      <Button variant="ghost" size="md" onclick={() => showAdvanced = !showAdvanced}>
        {showAdvanced ? 'Hide Advanced' : 'Advanced'}
      </Button>
    </div>
  </div>

  {#if showAdvanced}
    <div class="advanced-options">
      <div class="advanced-field">
        <FormInput
          label="Normalized Name"
          bind:value={formData.normalizedName}
          placeholder="e.g., 'Joe's Pizza'"
          disabled={loading}
        />
        <span class="field-hint">Clean up messy bank descriptions</span>
      </div>

      <div class="advanced-field recurring-field">
        <FormCheckbox
          label="Mark as Recurring"
          bind:checked={formData.markAsRecurring}
          disabled={loading}
        />
        {#if formData.markAsRecurring}
          <FormSelect
            label="Recurring Type"
            bind:value={formData.recurringType}
            options={[
              { value: '', label: 'Select type...' },
              { value: 'subscription', label: 'Subscription' },
              { value: 'bill', label: 'Bill' },
              { value: 'income', label: 'Income' },
              { value: 'transfer', label: 'Transfer' }
            ]}
            disabled={loading}
          />
        {/if}
      </div>

      <div class="advanced-field">
        <FormCheckbox
          label="Exclude from Analytics"
          bind:checked={formData.excludeFromAnalytics}
          hint="Hide from charts and reports"
          disabled={loading}
        />
      </div>
    </div>
  {/if}
</div>

<style lang="scss">
  .add-rule-section {
    margin-bottom: 25px;
  }

  .add-rule {
    display: flex;
    gap: var(--spacing-md);
    margin-bottom: 10px;
    align-items: flex-end;

    :global(.form-input),
    :global(.form-select),
    .form-field {
      flex: 1;
    }

    :global(.form-input:first-child) {
      flex: 2;
    }

    .button-group {
      display: flex;
      gap: var(--spacing-sm);
    }
  }

  .form-field {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xs);

    label {
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-secondary);
    }
  }

  .advanced-options {
    margin-top: 15px;
    padding: 15px;
    background: var(--color-background-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    display: grid;
    gap: 15px;
    grid-template-columns: 1fr 1fr 1fr;

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
    }
  }

  .advanced-field {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);

    .field-hint {
      font-size: var(--font-size-xxs);
      color: var(--color-text-tertiary);
      font-style: italic;
      margin-top: -4px;
    }
  }

  .recurring-field {
    :global(.form-select) {
      margin-top: 6px;
    }
  }
</style>
